// The member roster (RFC-1371 §6, §7.3).
//
// Every control is gated through features/teams/permissions, and the gate HIDES
// rather than disables. §6 asks that a Member "must not see controls they
// cannot use": a greyed-out Remove button still tells a Member that removal is
// something this team's administrators do to each other, and invites a click
// that can only 403.
//
// ── Some members have no name, permanently ─────────────────────────────────
//
// `display_name` / `email` / `avatar_url` are nullable, and null is a fact
// about the principal rather than a failed lookup: API-key principals, service
// accounts, the debug user and `PUBLIC_USER_ID` have no `auth.users` row at
// all. Rows render the best available label and fall through to a monospaced
// truncated id — never an error, never a retry. See features/teams/identity.ts.
//
// Removal confirms inline rather than in a dialog: the panel already lives
// inside `SettingsModal`, where a second live `<Modal>` would install a
// competing Escape handler and scroll lock (SettingsModal.tsx:103). And the
// thing most worth saying — §7.3's auto-publish — reads better next to the row
// it applies to than in a dialog that has lost the name.

import { useState } from 'react'
import MemberAllowanceControl from './MemberAllowanceControl'
import RoleSelect from './RoleSelect'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import { TeamsRequestError, type Team, type TeamMember, type TeamRole } from '../../api/teams'
import {
  assignableRoles,
  canRemoveMember,
  canSetAllowances,
  canSetAllowanceFor,
} from '../../features/teams/permissions'
import {
  memberLabel,
  memberLabelIsId,
  memberSecondaryLabel,
} from '../../features/teams/identity'
import {
  useChangeMemberRole,
  useRemoveMember,
  useTeamAllowances,
} from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { Card, EmptyState, ErrorText, SectionLabel } from './teamsChrome'
import { resultOf, trackInteraction } from '../../analytics/interactionAnalytics'

interface TeamMembersListProps {
  team: Team
  members: TeamMember[]
  isLoading: boolean
  /** The signed-in user, so their own row is labelled and never removable. */
  currentUserId: string | null
}

export default function TeamMembersList({
  team,
  members,
  isLoading,
  currentUserId,
}: TeamMembersListProps) {
  const { t } = useTranslation('settings')
  const changeRole = useChangeMemberRole()
  const removeMember = useRemoveMember()
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const ctx = { role: team.myRole, kind: team.kind }

  // `canSetAllowances` covers the personal-team case too — see its comment.
  // It is the FEATURE gate; whether this actor may cap a PARTICULAR member is
  // `canSetAllowanceFor`, applied per row below (an Admin may not cap the
  // Owner).
  const mayManageAllowances = canSetAllowances(ctx)
  const { data: allowances, isPending: allowancesPending } = useTeamAllowances(
    team.id,
    mayManageAllowances,
  )
  const allowanceFor = (userId: string) =>
    allowances?.find((a) => a.userId === userId) ?? null

  // WAIT FOR THE QUERY before mounting the editor, rather than passing null
  // through. `MemberAllowanceControl` seeds its draft from the prop ONCE, on
  // mount; if it mounts during the load it seeds from "uncapped", and a later
  // arrival updates the summary but not the draft. An admin would then open a
  // member who HAS a cap, see it rendered correctly, press Save, and overwrite
  // it with the default — losing the real cap to a UI race.
  const allowancesReady = mayManageAllowances && !allowancesPending

  const handleRoleChange = (member: TeamMember, nextRole: TeamRole) => {
    setError(null)
    const trackRole = (ok: boolean) => trackInteraction('teams_member_role_changed', {
      team_id: team.id, from_role: member.role, to_role: nextRole, actor_role: team.myRole, result: resultOf(ok),
    })
    changeRole.mutate(
      { teamId: team.id, userId: member.userId, role: nextRole },
      {
        onSuccess: () => trackRole(true),
        onError: (err) => {
          trackRole(false)
          if (!(err instanceof TeamsRequestError)) {
            setError(t('settingsModal.teams.roleChangeFailed'))
            return
          }
          // Distinct codes, distinct copy. "An admin cannot touch the owner
          // role" and "somebody else became owner while you were looking" are
          // different problems with different fixes.
          if (err.code === 'admin_cannot_change_owner_role') {
            setError(t('settingsModal.teams.roleAdminCannotTouchOwner'))
            return
          }
          setError(err.message_ ?? t('settingsModal.teams.roleChangeFailed'))
        },
      },
    )
  }

  const handleRemove = (member: TeamMember) => {
    setError(null)
    const trackRemove = (ok: boolean) => trackInteraction('teams_member_removed', {
      team_id: team.id, target_member_role: member.role, actor_role: team.myRole, result: resultOf(ok),
    })
    removeMember.mutate(
      { teamId: team.id, userId: member.userId },
      {
        onSuccess: () => { trackRemove(true); setPendingRemoval(null) },
        onError: (err) => {
          trackRemove(false)
          if (!(err instanceof TeamsRequestError)) {
            setError(t('settingsModal.teams.removeFailed'))
            return
          }
          if (err.code === 'cannot_remove_owner') {
            setError(t('settingsModal.teams.removeCannotRemoveOwner'))
            return
          }
          if (err.code === 'use_leave_to_remove_yourself') {
            setError(t('settingsModal.teams.removeUseLeaveInstead'))
            return
          }
          setError(err.message_ ?? t('settingsModal.teams.removeFailed'))
        },
      },
    )
  }

  if (isLoading) {
    return (
      <div>
        <SectionLabel>{t('settingsModal.teams.members')}</SectionLabel>
        <EmptyState testId="teams-members-loading">
          {t('settingsModal.teams.loadingMembers')}
        </EmptyState>
      </div>
    )
  }

  return (
    <div>
      <SectionLabel>
        {t('settingsModal.teams.membersWithCount', {
          count: members.length,
          limit: team.memberLimit,
        })}
      </SectionLabel>

      {members.length === 0 ? (
        <EmptyState testId="teams-members-empty">
          {t('settingsModal.teams.noMembers')}
        </EmptyState>
      ) : (
        <Card>
          {members.map((member, index) => {
            const isSelf = member.userId === currentUserId
            const roleOptions = assignableRoles(ctx, member)
            // Nobody changes their own role. An Owner demoting themselves would
            // leave the team ownerless, and D7 makes succession a consequence
            // of LEAVING, not of a dropdown.
            const mayChangeRole = !isSelf && roleOptions.length > 0
            const mayRemove = !isSelf && canRemoveMember(ctx, member)
            const confirming = pendingRemoval === member.userId
            const label = memberLabel(member)
            const labelIsId = memberLabelIsId(member)
            const secondary = memberSecondaryLabel(member)

            return (
              <div
                key={member.userId}
                data-testid={`teams-member-${member.userId}`}
                className={cn(
                  'flex flex-col gap-2 px-4 py-3',
                  index > 0 && 'border-t border-white/[0.06]',
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'truncate text-sm text-white/85',
                        // Monospace ONLY when the label is an id, so an id
                        // never reads as somebody's name.
                        labelIsId && 'font-mono',
                      )}
                    >
                      {label}
                      {isSelf && (
                        <span className="ml-1.5 font-sans text-xs font-medium text-white/40">
                          {t('settingsModal.teams.you')}
                        </span>
                      )}
                    </p>
                    {secondary !== null && (
                      <p className="truncate text-xs font-medium text-white/40">{secondary}</p>
                    )}
                  </div>

                  {mayChangeRole ? (
                    <RoleSelect
                      memberId={member.userId}
                      ariaLabel={t('settingsModal.teams.roleFor', { name: label })}
                      value={member.role}
                      options={roleOptions}
                      disabled={changeRole.isPending}
                      labelFor={(role) => t(`settingsModal.teams.role.${role}`)}
                      onSelect={(next) => handleRoleChange(member, next)}
                    />
                  ) : (
                    <span
                      data-testid={`teams-member-role-static-${member.userId}`}
                      className="rounded-md bg-white/[0.08] px-2 py-1 text-xs font-medium text-white/70"
                    >
                      {t(`settingsModal.teams.role.${member.role}`)}
                    </span>
                  )}

                  {mayRemove && !confirming && (
                    <button
                      type="button"
                      data-testid={`teams-member-remove-${member.userId}`}
                      aria-label={t('settingsModal.teams.removeMemberAria', { name: label })}
                      onClick={() => setPendingRemoval(member.userId)}
                      className="shrink-0 cursor-pointer rounded-lg p-1.5 text-white/40 transition-colors hover:bg-destructive/15 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {allowancesReady && canSetAllowanceFor(ctx, member.role) && (
                  <MemberAllowanceControl
                    teamId={team.id}
                    userId={member.userId}
                    allowance={allowanceFor(member.userId)}
                    memberLabel={label}
                    onError={setError}
                    actorRole={team.myRole}
                    targetRole={member.role}
                  />
                )}

                {confirming && (
                  <div
                    data-testid={`teams-member-remove-confirm-${member.userId}`}
                    className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2.5"
                  >
                    {/* §7.3: an admin removal auto-publishes the departing
                        member's private projects, irreversibly. The person
                        clicking is not the person affected, so the consequence
                        has to be on screen before the click. */}
                    <p className="text-sm leading-6 text-white/85">
                      {t('settingsModal.teams.removeConfirm', { name: label })}
                    </p>
                    <p className="mt-1 text-xs font-medium leading-5 text-white/60">
                      {t('settingsModal.teams.removeAutoPublishNotice')}
                    </p>
                    <div className="mt-2.5 flex justify-end gap-2">
                      <button
                        type="button"
                        data-testid={`teams-member-remove-cancel-${member.userId}`}
                        onClick={() => setPendingRemoval(null)}
                        className="h-8 cursor-pointer rounded-full bg-white/[0.12] px-4 text-xs font-medium text-white transition-colors hover:bg-white/20"
                      >
                        {t('settingsModal.teams.cancel')}
                      </button>
                      <button
                        type="button"
                        data-testid={`teams-member-remove-submit-${member.userId}`}
                        disabled={removeMember.isPending}
                        onClick={() => handleRemove(member)}
                        className={cn(
                          'h-8 cursor-pointer rounded-full bg-destructive px-4 text-xs font-semibold text-white',
                          'transition-colors hover:bg-destructive/90',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                        )}
                      >
                        {t('settingsModal.teams.removeMember')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </Card>
      )}

      {error !== null && <ErrorText testId="teams-members-error">{error}</ErrorText>}
    </div>
  )
}
